import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import { TbReportAnalytics } from 'react-icons/tb';
import { BsPen, BsTrash3, BsSend } from 'react-icons/bs';
import { AiOutlineLogin } from 'react-icons/ai';
import { GoHistory } from 'react-icons/go';
import dayjs from 'dayjs';

import { Pagination, SortProps, TableColumn } from 'types/table';
import { forgotPassword, proxyLogin, sendWelcome } from 'api/auth';
import { Student } from 'api/students/types';
import { checkboxProps } from 'utils';
import { useAuthStore } from 'store/auth';

import { TableActions } from 'components/ui/TableActions';
import { Checkbox } from 'components/ui/Checkbox';
import Table from 'components/ui/Table';
import { maskValue } from 'utils/mask';
interface Props {
  isLoading: boolean;
  sort?: SortProps;
  data: Student[];
  onUpdate: (student: Student) => void;
  onDelete: (id: string) => void;
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  pagination?: Pagination;
  setLoginHistory: React.Dispatch<React.SetStateAction<{ isOpen: boolean; data: Student['loginHistory'] }>>;
}

export const StudentTable = ({
  isLoading,
  data,
  sort,
  onUpdate,
  onDelete,
  selected,
  setSelected,
  pagination,
  setLoginHistory
}: Props) => {
  const [activeMenu, setActiveMenu] = useState('');
  const { mutate: proxyLoginMutate } = useMutation(proxyLogin);
  const { user } = useAuthStore();
  const { mutate: forgotPasswordMutate } = useMutation(forgotPassword);
  const { mutate: sendWelcomeMutate } = useMutation(sendWelcome);
  const deepMask = (value: any, keyName?: string): any => {
    if (value === null || value === undefined) return value;
    if (keyName === 'timestamp') return value;


    if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) {
      return maskValue(value);
    }

    if (Array.isArray(value)) {
      return value.map((v) => deepMask(v));
    }

    if (typeof value === 'object') {
      const maskedObj: Record<string, any> = {};
      for (const key in value) {
        maskedObj[key] = deepMask(value[key], key);
      }
      return maskedObj;
    }

    return value;
  };
  const maskedData = user?.isCensorshipEnabled
    ? data.map((student) => {
      const masked = deepMask(student);
      return { ...masked, _id: student._id };
    })
    : data;
  const navigate = useNavigate();
  const columns: TableColumn[] = [
        {
      key: 'name',
      name: 'Student Name',
      sort: ['name'],
      cell: (el: Student, i) => {
        const canViewReport =
          user?.userRole === 'admin' ||
          user?.userRole === 'moderator' ||
          user?.userRole === 'organizationAdmin';

        return (
          <div className="flex items-center gap-3">
            {user?.userRole !== 'moderator' ? (
              <Checkbox {...checkboxProps(el._id, selected, setSelected)} />
            ) : null}
            
            <span className="badge primary !p-3">0{(i + 1).toLocaleString()}</span>
            {canViewReport ? (
              <span
                className="cursor-pointer hover:underline text-blue-600 font-medium transition-colors"
                onClick={() => navigate(`/student-reports/${el?._id}`)}
                title="View Student Report"
              >
                {el.name}
              </span>
            ) : (
              <span>{el.name}</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'email',
      name: 'Email',
      sort: ['email'],
      cell: (el: Student) => el.email
    },
    {
      key: 'group',
      name: 'Group Name',
      sort: ['group.name'],
      cell: (el: Student) => el.group?.name || 'N/A'
    },
    {
      key: 'organization',
      name: 'Org Name',
      sort: ['organization.name'],
      cell: (el: Student) => el.organization?.name || 'N/A'
    },
    {
      key: 'subscriptionPlan',
      name: 'Membership Plan',
      cell: (el: Student) => {
        // Guard against dangling references: a membership plan that has been
        // deleted populates as `null` on the server, so skip those entries.
        const plans = (el.subscription?.membershipPlans || []).filter((v) => v?.membership);
        return (
          <div className="flex flex-col">
            {plans.length > 0
              ? plans.map((v) => <div key={v.membership._id}>{v.membership.name}</div>)
              : 'N/A'}
          </div>
        );
      }
    },
    {
      key: 'userLogin',
      name: 'User Login',
      align: 'center',
      sort: ['loginHistory.length'],
      cell: (el: Student) => (
        <span className="cursor-pointer" onClick={() => setLoginHistory({ data: el.loginHistory, isOpen: true })}>
          {el.loginHistory.length}
        </span>
      )
    },
    {
      key: 'lastLogin',
      name: 'Last Login',
      align: 'center',
      cell: (el: Student) => {
        const history = el.loginHistory || [];
        if (!history.length) return 'N/A';

        const lastLogin = history[history.length - 1]?.timestamp;
        if (!lastLogin) return 'N/A';

        if (user?.isCensorshipEnabled) return dayjs(lastLogin).format('** ***, ****');
        return dayjs(lastLogin).format('DD MMM, YYYY');
      }
    },
    {
      key: 'status',
      name: 'Status',
      align: 'center',
      cell: (el: Student) => (
        <div className={`badge ${el.isActive ? 'success' : 'danger'}`}>{el.isActive ? 'Active' : 'Inactive'}</div>
      )
    },
    {
      key: 'actions',
      name: '',
      align: 'center',
      cell: (el: Student) => (
        <TableActions activeMenu={activeMenu} setActiveMenu={setActiveMenu} itemID={el._id}>
          {(user?.userRole === 'admin' || user?.userRole === 'organizationAdmin' || user?.userRole === 'moderator') && (
            <li onClick={() => proxyLoginMutate(el._id)}>
              <AiOutlineLogin /> Proxy Login
            </li>
          )}

          {user?.userRole === 'admin' || user?.userRole === 'moderator' || user?.userRole === 'organizationAdmin' ? (
            <li onClick={() => navigate(`/student-reports/${el?._id}`)}>
              <TbReportAnalytics /> Student Report
            </li>
          ) : null}

          {(user?.userRole === 'admin' || user?.userRole === 'moderator') && (
            <li onClick={() => onUpdate(el)}>
              <BsPen className="text-light-200" /> Edit Student
            </li>
          )}
          {(user?.userRole === 'admin' || user?.userRole === 'moderator') && (
            <li onClick={() => onDelete(el._id)}>
              <BsTrash3 className="text-danger" />
              Delete Student
            </li>
          )}
          {user?.userRole === 'admin' || user?.userRole === 'moderator' ? (
            <li onClick={() => setLoginHistory({ data: el.loginHistory, isOpen: true })}>
              <GoHistory />
              Login history
            </li>
          ) : null}
          {(user?.userRole === 'admin' || user?.userRole === 'moderator') && (
            <li onClick={() => forgotPasswordMutate(el.email)}>
              <BsSend />
              Send Password Reset
            </li>
          )}
          {(user?.userRole === 'admin' || user?.userRole === 'moderator') && (
            <li onClick={() => sendWelcomeMutate(el.email)}>
              <BsSend />
              Send Welcome Email
            </li>
          )}
        </TableActions>
      )
    }
  ];
  const modifiedColumns =
    user?.userRole === 'organizationAdmin'
      ? columns.filter((column) => column.key !== 'organization') // Remove 'Organization' column
      : columns;

  return (
    <>
      <Table
        pagination={pagination}
        isLoading={isLoading}
        sort={sort}
        data={maskedData}
        columns={modifiedColumns}
        className={user?.userRole === 'organizationAdmin' ? 'orgStudentTable' : 'studentTable'}
      />
    </>
  );
};
